import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  ShieldCheck,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Ban,
  RefreshCw
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAccreditationPage = () => {
  const { t } = useI18n();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/admin/all-companies`);
      setCompanies(res.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error(t('common.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  // Filtra aziende per tab e ricerca
  const getFilteredCompanies = () => {
    let filtered = companies;
    
    // Filtra per tab
    if (activeTab === 'pending') {
      filtered = filtered.filter(c => c.accreditation_status === 'pending');
    } else if (activeTab === 'approved') {
      filtered = filtered.filter(c => c.is_accredited);
    } else if (activeTab === 'rejected') {
      filtered = filtered.filter(c => c.accreditation_status === 'rejected');
    }
    // 'all' mostra tutto
    
    // Filtra per ricerca
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.type?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const handleApprove = async (companyId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/accreditation/${companyId}/approve`);
      toast.success(t('settings.accreditationApproved'));
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedCompany) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/accreditation/${selectedCompany.id}/reject`, {
        notes: rejectNotes
      });
      toast.success(t('settings.accreditationRejected'));
      setShowRejectDialog(false);
      setRejectNotes('');
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setProcessing(false);
    }
  };

  const handleRevoke = async () => {
    if (!selectedCompany) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/accreditation/${selectedCompany.id}/revoke`, {
        notes: rejectNotes
      });
      toast.success(t('settings.accreditationRevoked'));
      setShowRevokeDialog(false);
      setRejectNotes('');
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (company) => {
    if (company.is_accredited) {
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Accreditato</Badge>;
    }
    if (company.accreditation_status === 'pending') {
      return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">In Attesa</Badge>;
    }
    if (company.accreditation_status === 'rejected') {
      return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Rifiutato</Badge>;
    }
    return <Badge variant="outline">Non richiesto</Badge>;
  };

  const getTypeBadge = (type) => {
    const types = {
      buyer: { label: 'Buyer', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
      producer: { label: 'Produttore', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
      distributor: { label: 'Distributore', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
      agent: { label: 'Agente', color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
      other: { label: 'Altro', color: 'bg-gray-500/10 text-gray-600 border-gray-500/20' }
    };
    const t = types[type] || types.other;
    return <Badge className={t.color}>{t.label}</Badge>;
  };

  const pendingCount = companies.filter(c => c.accreditation_status === 'pending').length;
  const approvedCount = companies.filter(c => c.is_accredited).length;
  const rejectedCount = companies.filter(c => c.accreditation_status === 'rejected').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filteredCompanies = getFilteredCompanies();

  return (
    <div className="space-y-6" data-testid="admin-accreditation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <ShieldCheck className="h-8 w-8" />
            Gestione Accreditamenti
          </h1>
          <p className="text-muted-foreground mt-1">
            Approva o rifiuta le richieste di accreditamento dei fornitori
          </p>
        </div>
        <Button variant="outline" onClick={fetchCompanies}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Aggiorna
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Attesa</p>
                <p className="text-3xl font-bold text-amber-600">{pendingCount}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Accreditati</p>
                <p className="text-3xl font-bold text-green-600">{approvedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rifiutati</p>
                <p className="text-3xl font-bold text-red-600">{rejectedCount}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Aziende</p>
                <p className="text-3xl font-bold">{companies.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Search */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="pending" className="gap-2">
                  <Clock className="h-4 w-4" />
                  In Attesa
                  {pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 justify-center">
                      {pendingCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="approved">Accreditati</TabsTrigger>
                <TabsTrigger value="rejected">Rifiutati</TabsTrigger>
                <TabsTrigger value="all">Tutti</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca azienda..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCompanies.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna azienda trovata</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Azienda</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Contatto</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Data Richiesta</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt="" className="w-10 h-10 rounded object-contain bg-muted" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{company.name}</p>
                          {company.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {company.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(company.type)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {company.email && <p>{company.email}</p>}
                        {company.phone && <p className="text-muted-foreground">{company.phone}</p>}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(company)}</TableCell>
                    <TableCell>
                      {company.accreditation_requested_at ? (
                        <span className="text-sm">
                          {new Date(company.accreditation_requested_at).toLocaleDateString('it-IT')}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {company.accreditation_status === 'pending' ? (
                        <div className="flex justify-end gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleApprove(company.id)}
                            disabled={processing}
                          >
                            <CheckCircle className="mr-1 h-4 w-4" />
                            Approva
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => {
                              setSelectedCompany(company);
                              setShowRejectDialog(true);
                            }}
                            disabled={processing}
                          >
                            <XCircle className="mr-1 h-4 w-4" />
                            Rifiuta
                          </Button>
                        </div>
                      ) : company.is_accredited ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => window.open(`/directory/${company.id}`, '_blank')}>
                              <Eye className="mr-2 h-4 w-4" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => {
                                setSelectedCompany(company);
                                setShowRevokeDialog(true);
                              }}
                            >
                              <Ban className="mr-2 h-4 w-4" />
                              Revoca Accreditamento
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : company.accreditation_status === 'rejected' ? (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleApprove(company.id)}
                          disabled={processing}
                        >
                          <CheckCircle className="mr-1 h-4 w-4" />
                          Riconsidera
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              Rifiuta Accreditamento
            </DialogTitle>
            <DialogDescription>
              Stai per rifiutare la richiesta di accreditamento di <strong>{selectedCompany?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Motivazione (opzionale)</label>
            <Textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Inserisci una motivazione per il rifiuto..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing}>
              {processing ? 'Elaborazione...' : 'Conferma Rifiuto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Ban className="h-5 w-5" />
              Revoca Accreditamento
            </DialogTitle>
            <DialogDescription>
              Stai per revocare l'accreditamento di <strong>{selectedCompany?.name}</strong>.
              L'azienda non sarà più visibile nella directory fornitori.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Motivazione (opzionale)</label>
            <Textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Inserisci una motivazione per la revoca..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevokeDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={processing}>
              {processing ? 'Elaborazione...' : 'Conferma Revoca'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAccreditationPage;
